import React, { type PropsWithChildren, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import invariant from 'tiny-invariant';

import {
	attachClosestEdge,
	type Edge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type { Item } from '../../../types';
import { DragHandleMenu } from '../../config-panel/drag-handle-menu';

import { getItemData, isItemData } from './draggable-item-data';

type DraggableState =
	| {
			type: 'idle';
	  }
	| {
			container: HTMLElement;
			type: 'preview';
	  }
	| {
			type: 'is-dragging';
	  }
	| {
			closestEdge: Edge | null;
			type: 'is-dragging-over';
	  };

const idle: DraggableState = { type: 'idle' };
const dragging: DraggableState = { type: 'is-dragging' };

const itemContainer = xcss({
	alignItems: 'flex-start',
	position: 'relative',
});

const itemContainerV2 = xcss({
	alignItems: 'flex-start',
	position: 'relative',
	paddingTop: 'space.200',
	paddingBottom: 'space.200',
});

const itemDisabled = xcss({ opacity: 0.4 });

const itemPreview = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	borderRadius: 'radius.small',
	backgroundColor: 'elevation.surface.overlay',
	maxWidth: '360px',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const draggableContent = xcss({
	width: '100%',
});

export type DraggableItemContainerProps = PropsWithChildren<{
	headerTitle: string;
	index: number;
	isDragHandleFocused: boolean;
	isFirstItem: boolean;
	isLastItem: boolean;
	item: Item;
	onBlur(): void;
	onFocus(): void;
	setDraggedItem(): void;
	setIsDragHandleFocused(focused: boolean): void;
	shouldUseCardV2UI?: boolean;
	swapItems(source: number, target: number): void;
}>;

export const DraggableItemContainer = ({
	item,
	children,
	headerTitle,
	index,
	isDragHandleFocused,
	isFirstItem,
	isLastItem,
	setDraggedItem,
	setIsDragHandleFocused,
	swapItems,
	shouldUseCardV2UI,
	onBlur,
	onFocus,
}: DraggableItemContainerProps): React.JSX.Element => {
	const ref = useRef<HTMLDivElement>(null);

	const [draggableState, setDraggableState] = useState<DraggableState>(idle);

	useEffect(() => {
		const element = ref.current;
		invariant(element);

		const data = getItemData({ item, index });

		return combine(
			draggable({
				element,
				getInitialData: () => data,
				onGenerateDragPreview: ({ nativeSetDragImage }) => {
					setCustomNativeDragPreview({
						nativeSetDragImage,
						getOffset: pointerOutsideOfPreview({
							x: token('space.200'),
							y: token('space.100'),
						}),
						render: ({ container }) => {
							setDraggableState({ type: 'preview', container });
						},
					});
				},
				onDragStart: () => {
					setDraggableState(dragging);
					setIsDragHandleFocused(false);
				},
				onDrop: () => {
					setDraggableState(idle);
					setDraggedItem();
				},
			}),
			dropTargetForElements({
				element,
				canDrop: ({ source }) => {
					if (source.element === element) {
						return false;
					}

					return isItemData(source.data);
				},
				getData: ({ input }) => {
					return attachClosestEdge(data, {
						element,
						input,
						allowedEdges: ['top', 'bottom'],
					});
				},
				getIsSticky: () => {
					return true;
				},
				onDragEnter: ({ self }) => {
					const closestEdge = extractClosestEdge(self.data);
					setDraggableState({ type: 'is-dragging-over', closestEdge });
					setIsDragHandleFocused(false);
				},
				onDrag: ({ self }) => {
					const closestEdge = extractClosestEdge(self.data);

					setDraggableState((current) => {
						if (current.type === 'is-dragging-over' && current.closestEdge === closestEdge) {
							return current;
						}
						return { type: 'is-dragging-over', closestEdge };
					});
				},
				onDragLeave: () => {
					setDraggableState(idle);
				},
				onDrop: () => {
					setDraggableState(idle);
					setDraggedItem();
				},
			}),
		);
	}, [item, index, setDraggedItem, setIsDragHandleFocused]);

	const isDragging = draggableState.type === 'is-dragging';
	const handleMove = (sourceIndex: number, targetIndex: number) => {
		setDraggedItem();
		setIsDragHandleFocused(false);
		swapItems(sourceIndex, targetIndex);
	};

	return (
		<>
			<Stack
				grow="fill"
				alignBlock="stretch"
				xcss={[shouldUseCardV2UI ? itemContainerV2 : itemContainer]}
			>
				<Stack ref={ref} alignInline="start" alignBlock="center" grow="fill">
					<Inline alignBlock="center" alignInline="stretch" xcss={draggableContent}>
						<DragHandleMenu
							isDragging={isDragging}
							isDragHandleFocused={isDragHandleFocused}
							isFirst={isFirstItem}
							isLast={isLastItem}
							onMoveDown={() => {
								handleMove(index, index + 1);
							}}
							onMoveUp={() => {
								handleMove(index, index - 1);
							}}
							setIsDragHandleFocused={setIsDragHandleFocused}
							onBlur={onBlur}
							onFocus={onFocus}
						/>
						<Box xcss={[isDragging && itemDisabled, draggableContent]}>{children}</Box>
						{draggableState.type === 'is-dragging-over' && draggableState.closestEdge ? (
							<DropIndicator
								edge={draggableState.closestEdge}
								{...(!shouldUseCardV2UI && { gap: token('space.200') })}
							/>
						) : null}
					</Inline>
				</Stack>
			</Stack>
			{draggableState.type === 'preview' &&
				createPortal(<Box xcss={itemPreview}>{headerTitle}</Box>, draggableState.container)}
		</>
	);
};
